/**
 * HTTP client for the Kolectra API (web). Mirrors the desktop client's §20.3 error
 * envelope handling, but reads the access token from the token-store and, on a
 * 401, transparently refreshes once before retrying. The refresh itself is
 * provided by the AuthProvider via `setRefreshHandler` (so this module has no
 * dependency on React state).
 */

import type {
  AuthTokens,
  SignInResult,
  SessionInfo,
  ChallengeCreateRequest,
  ChallengeListResponse,
  ChallengeSummary,
  ChallengeDesign,
  ChallengeUpdateInput,
  JudgementCreateInput,
  ChallengeReview,
  PreTestReview,
  UtteranceSetRequest,
  UtteranceSummary,
  MemberSummary,
  MemberCreateRequest,
  MemberUpdateInput,
  RoleSummary,
  TeamDashboard,
  ProjectDashboard,
  ProjectSyncStatus,
} from '@kolectra/shared/schemas';
import type { ImportStageResponse, ImportCommitResponse, ExportSheetResponse } from '@kolectra/shared/import';
import { getAccessToken } from './token-store.js';

const API_URL: string =
  (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3030/api/v1';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Set by the AuthProvider: refresh the session, returning true on success. */
type RefreshHandler = () => Promise<boolean>;
let refreshHandler: RefreshHandler | null = null;
export function setRefreshHandler(fn: RefreshHandler | null): void {
  refreshHandler = fn;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  idempotencyKey?: string;
  signal?: AbortSignal;
  /** Attach the bearer token (default true). Public endpoints pass false. */
  auth?: boolean;
  /** Internal: prevents an infinite refresh loop. */
  _retry?: boolean;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.auth !== false) {
    const token = getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  if (opts.idempotencyKey) headers['X-Idempotency-Key'] = opts.idempotencyKey;

  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (err) {
    throw new ApiError(0, 'NETWORK_ERROR', 'Could not reach the server.', { cause: String(err) });
  }

  // Expired access token: refresh once, then replay the original request.
  if (res.status === 401 && opts.auth !== false && !opts._retry && refreshHandler) {
    const refreshed = await refreshHandler();
    if (refreshed) return request<T>(path, { ...opts, _retry: true });
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const json = text ? safeParse(text) : undefined;

  if (!res.ok) {
    const envelope = (json as { error?: { code?: string; message?: string; details?: Record<string, unknown> } })?.error;
    throw new ApiError(
      res.status,
      envelope?.code ?? 'UNKNOWN',
      envelope?.message ?? `Request failed with status ${res.status}.`,
      envelope?.details,
    );
  }

  return json as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

// --- Typed endpoint helpers ---------------------------------------------

export const authApi = {
  signIn: (email: string, password: string, deviceLabel?: string) =>
    request<SignInResult>('/auth/sign-in', {
      method: 'POST',
      auth: false,
      body: { email, password, deviceLabel },
    }),

  refresh: (refreshToken: string) =>
    request<AuthTokens>('/auth/refresh', { method: 'POST', auth: false, body: { refreshToken } }),

  signOut: (refreshToken: string) =>
    request<void>('/auth/sign-out', { method: 'POST', auth: false, body: { refreshToken } }),

  me: () => request<SessionInfo>('/auth/me'),
};

export const challengesApi = {
  list: (teamId: string, cursor?: string) =>
    request<ChallengeListResponse>(
      `/teams/${teamId}/challenges${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
    ),

  create: (teamId: string, body: ChallengeCreateRequest, idempotencyKey: string) =>
    request<ChallengeSummary>(`/teams/${teamId}/challenges`, {
      method: 'POST',
      body,
      idempotencyKey,
    }),

  getDesign: (challengeId: string) =>
    request<ChallengeDesign>(`/challenges/${challengeId}/design`),

  update: (challengeId: string, body: ChallengeUpdateInput) =>
    request<ChallengeSummary>(`/challenges/${challengeId}`, { method: 'PATCH', body }),

  /** Publish a draft for recording (DRAFT → READY_TO_RECORD). "Approve Segment" for a reviewer. */
  submit: (challengeId: string, idempotencyKey: string) =>
    request<ChallengeSummary>(`/challenges/${challengeId}/submit`, { method: 'POST', idempotencyKey }),

  /** Approve the recording and advance to testing (RECORDING → READY_TO_TEST). "Approve Audio". */
  confirmRecording: (challengeId: string, idempotencyKey: string) =>
    request<ChallengeSummary>(`/challenges/${challengeId}/confirm-recording`, { method: 'POST', idempotencyKey }),

  // Judgement (online; not queued).
  review: (challengeId: string) => request<ChallengeReview>(`/challenges/${challengeId}/review`),

  /** Pre-test QC review: full design + recordings + who-did-what (no model output). */
  preTestReview: (challengeId: string) =>
    request<PreTestReview>(`/challenges/${challengeId}/pre-test-review`),

  judge: (challengeId: string, idempotencyKey: string, body: JudgementCreateInput) =>
    request<ChallengeSummary>(`/challenges/${challengeId}/judge`, { method: 'POST', idempotencyKey, body }),

  approve: (challengeId: string, idempotencyKey: string) =>
    request<ChallengeSummary>(`/challenges/${challengeId}/approve`, { method: 'POST', idempotencyKey }),

  tiebreak: (challengeId: string, idempotencyKey: string, body: JudgementCreateInput) =>
    request<ChallengeSummary>(`/challenges/${challengeId}/tiebreak`, { method: 'POST', idempotencyKey, body }),

  /** Send a non-terminal challenge back to DRAFT with a required reason (can_force_rework). */
  rework: (challengeId: string, idempotencyKey: string, comment: string) =>
    request<ChallengeSummary>(`/challenges/${challengeId}/rework`, { method: 'POST', idempotencyKey, body: { comment } }),

  /** Send a READY_TO_TEST challenge back to RECORDING for a re-record (can_reject_recording). */
  rejectRecording: (challengeId: string, idempotencyKey: string, comment: string) =>
    request<ChallengeSummary>(`/challenges/${challengeId}/reject-recording`, { method: 'POST', idempotencyKey, body: { comment } }),

  /** "Re-record": reject the segment audio with a comment (can_reject_recording). Works from RECORDING or READY_TO_TEST. */
  rejectSegmentAudio: (challengeId: string, idempotencyKey: string, comment: string) =>
    request<ChallengeSummary>(`/challenges/${challengeId}/reject-segment-audio`, { method: 'POST', idempotencyKey, body: { comment } }),
};

export const utterancesApi = {
  /** Replace a draft challenge's utterance set (design seam, can_design). */
  set: (challengeId: string, body: UtteranceSetRequest) =>
    request<UtteranceSummary[]>(`/challenges/${challengeId}/utterances`, { method: 'POST', body }),
};

export const importApi = {
  /** Stage an uploaded file (base64 XLSX or CSV): parse + validate, returns the report. */
  stage: (teamId: string, fileBase64: string, filename: string) =>
    request<ImportStageResponse>(`/teams/${teamId}/imports`, { method: 'POST', body: { fileBase64, filename } }),

  /** Commit a staged import into DRAFT challenges (idempotent). */
  commit: (teamId: string, importId: string) =>
    request<ImportCommitResponse>(`/teams/${teamId}/imports/${importId}/commit`, {
      method: 'POST',
      body: {},
    }),
};

export const exportApi = {
  /** Export the team's challenges as a prep-sheet XLSX (published-only unless includeDrafts). */
  sheet: (teamId: string, includeDrafts: boolean) =>
    request<ExportSheetResponse>(
      `/teams/${teamId}/export/sheet?includeDrafts=${includeDrafts ? 'true' : 'false'}`,
    ),
};

export const adminApi = {
  /** Team members (team-lead console). */
  members: (teamId: string) => request<MemberSummary[]>(`/teams/${teamId}/members`),

  /** Assignable roles: system roles + this team's custom roles. */
  roles: (teamId: string) => request<RoleSummary[]>(`/teams/${teamId}/roles`),

  createMember: (teamId: string, body: MemberCreateRequest) =>
    request<MemberSummary>(`/teams/${teamId}/members`, { method: 'POST', body }),

  updateMember: (teamId: string, userId: string, body: MemberUpdateInput) =>
    request<MemberSummary>(`/teams/${teamId}/members/${userId}`, { method: 'PATCH', body }),

  removeMember: (teamId: string, userId: string) =>
    request<void>(`/teams/${teamId}/members/${userId}`, { method: 'DELETE' }),
};

export const dashboardApi = {
  /** A team's read-only progress funnel (gated can_view_dashboard). */
  team: (teamId: string, languoidCode?: string) =>
    request<TeamDashboard>(
      `/teams/${teamId}/dashboard${languoidCode ? `?languoidCode=${encodeURIComponent(languoidCode)}` : ''}`,
    ),
  /** The project-wide funnel (gated can_view_project_reports). */
  project: (opts: { teamId?: string; languoidCode?: string } = {}) => {
    const q = new URLSearchParams();
    if (opts.teamId) q.set('teamId', opts.teamId);
    if (opts.languoidCode) q.set('languoidCode', opts.languoidCode);
    const qs = q.toString();
    return request<ProjectDashboard>(`/project/dashboard${qs ? `?${qs}` : ''}`);
  },
};

export const projectSyncApi = {
  /** Project sync status + per-source log + next auto-sync time (gated can_view_project_reports). */
  status: () => request<ProjectSyncStatus>('/project/sync'),
  /** Run every team's enabled sources now, then return the fresh status. */
  run: () => request<ProjectSyncStatus>('/project/sync', { method: 'POST' }),
};

/** A short idempotency key for one-shot mutations from the web UI. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
